const URI_PREFIXES = [
  '',
  'http://www.',
  'https://www.',
  'http://',
  'https://',
  'tel:',
  'mailto:',
  'ftp://anonymous:anonymous@',
  'ftp://ftp.',
  'ftps://',
  'sftp://',
  'smb://',
  'nfs://',
  'ftp://',
  'dav://',
  'news:',
  'telnet://',
  'imap:',
  'rtsp://',
  'urn:',
  'pop:',
  'sip:',
  'sips:',
  'tftp:',
  'btspp://',
  'btl2cap://',
  'btgoep://',
  'tcpobex://',
  'irdaobex://',
  'file://',
  'urn:epc:id:',
  'urn:epc:tag:',
  'urn:epc:pat:',
  'urn:epc:raw:',
  'urn:epc:',
  'urn:nfc:',
];

export const NFC_READER_STATES = Object.freeze({
  IDLE: 'IDLE',
  SCANNING: 'SCANNING',
  URI_READ: 'URI_READ',
  SUBMITTING: 'SUBMITTING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  CANCELLED: 'CANCELLED',
  UNAVAILABLE: 'UNAVAILABLE',
});

export const NFC_READER_ERRORS = Object.freeze({
  NOT_AVAILABLE: 'NFC_NON_DISPONIBILE',
  SCAN_IN_PROGRESS: 'NFC_SCAN_IN_PROGRESS',
  CANCELLED: 'NFC_CANCELLED',
  TIMEOUT: 'NFC_TIMEOUT',
  INVALID_RESULT: 'NFC_INVALID_RESULT',
  INVALID_NDEF_RECORD: 'NDEF_RECORD_NOT_URI',
  INVALID_NFC_URI: 'NFC_URI_NON_VALIDA',
  INVALID_TOKEN: 'NFC_TOKEN_NON_VALIDO',
  CONFIGURATION: 'NFC_HOST_NON_CONFIGURATO',
  SUBMIT_TIMEOUT: 'NFC_SUBMIT_TIMEOUT',
  PREFLIGHT_UNAVAILABLE: 'NFC_PREFLIGHT_NON_DISPONIBILE',
  PREFLIGHT_INVALID_RESULT: 'NFC_PREFLIGHT_RISULTATO_NON_VALIDO',
});

export const NFC_SIMPLE_TOKEN_LENGTH = 43;
export const NFC_SIMPLE_URI_PATH = '/nfc/';
export const NFC_SUN_HOST = 'restaurantos.it';
export const NFC_SUN_PATH = '/nfc';

const TOKEN_PATTERN = new RegExp(`^[A-Za-z0-9_-]{${NFC_SIMPLE_TOKEN_LENGTH}}$`);

export class NfcReaderError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = 'NfcReaderError';
    this.code = code;
    this.cause = cause;
  }
}

function getGlobalObject() {
  if (typeof window !== 'undefined') return window;
  if (typeof globalThis !== 'undefined') return globalThis;
  return {};
}

export function getNativeNfcPlugin() {
  const root = getGlobalObject();
  return root.Capacitor?.Plugins?.RestaurantOSNfcReader
    || root.RestaurantOSNfcReader
    || null;
}

function toBytes(value) {
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (Array.isArray(value)) return new Uint8Array(value);
  if (typeof value === 'string') {
    try {
      return Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
    } catch (error) {
      return null;
    }
  }
  return null;
}

function decodeUtf8(bytes) {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(bytes);
  }

  const binary = String.fromCharCode(...bytes);
  try {
    return decodeURIComponent(
      binary.replace(/[\u0080-\u00ff]/g, (character) => (
        `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`
      )),
    );
  } catch (error) {
    return binary;
  }
}

function recordTypeIsUri(record) {
  const type = record?.type;
  if (typeof type === 'string') {
    return type === 'U' || type.toLowerCase() === 'urn:nfc:wkt:u';
  }
  const typeBytes = toBytes(type);
  return typeBytes?.length === 1 && typeBytes[0] === 0x55;
}

function configuredAllowedHosts() {
  const configured = process.env.REACT_APP_NFC_SIMPLE_ALLOWED_HOSTS || '';
  return configured
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
}

function currentAppHost() {
  if (typeof window === 'undefined' || !window.location?.host) return '';
  return window.location.host.toLowerCase();
}

export function getAllowedNfcHosts() {
  const configured = configuredAllowedHosts();
  return Array.from(new Set([
    NFC_SUN_HOST,
    ...configured,
    ...(!configured.length ? [currentAppHost()] : []),
  ]));
}

export function validateSunNfcUri(rawUri) {
  if (typeof rawUri !== 'string' || rawUri.length > 2048 || /[^!-~]/.test(rawUri)) {
    throw new NfcReaderError(
      NFC_READER_ERRORS.INVALID_NFC_URI,
      'La lettura SUN NFC non è valida.',
    );
  }
  let parsed;
  try {
    parsed = new URL(rawUri);
  } catch (error) {
    throw new NfcReaderError(
      NFC_READER_ERRORS.INVALID_NFC_URI,
      'La URI NFC non è valida.',
      error,
    );
  }

  const allowedHosts = getAllowedNfcHosts();
  const sunUrlPattern = /^https:\/\/restaurantos\.it\/nfc\?picc_data=([0-9a-fA-F]{32})&enc=([0-9a-fA-F]{32})&cmac=([0-9a-fA-F]{16})$/;
  if (
    parsed.protocol !== 'https:'
    || parsed.hostname.toLowerCase() !== NFC_SUN_HOST
    || !allowedHosts.includes(parsed.host.toLowerCase())
    || parsed.port
    || parsed.username
    || parsed.password
    || parsed.hash
    || parsed.pathname !== NFC_SUN_PATH
    || !sunUrlPattern.test(rawUri)
  ) {
    throw new NfcReaderError(
      NFC_READER_ERRORS.INVALID_NFC_URI,
      'La URI deve contenere una lettura SUN RestaurantOS valida.',
    );
  }

  return { sunUrl: rawUri };
}

// Legacy parsing remains available to render old links as disabled. It is not
// used for attendance, which now accepts only a verified SUN URL.
export function validateNfcSimpleToken(value) {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(value)) {
    throw new NfcReaderError(NFC_READER_ERRORS.INVALID_NFC_URI, 'Tag NFC legacy non valido');
  }
  return value;
}

export function decodeNdefUriRecord(record) {
  if (!recordTypeIsUri(record)) {
    throw new NfcReaderError(
      NFC_READER_ERRORS.INVALID_NDEF_RECORD,
      'Il record NDEF non è una URI.',
    );
  }

  const payload = toBytes(record?.payload ?? record?.data);
  if (!payload || payload.length < 1 || !URI_PREFIXES[payload[0]]) {
    throw new NfcReaderError(
      NFC_READER_ERRORS.INVALID_NDEF_RECORD,
      'Il record NDEF URI è vuoto o non supportato.',
    );
  }

  const suffix = decodeUtf8(payload.slice(1));
  return validateSunNfcUri(`${URI_PREFIXES[payload[0]]}${suffix}`);
}

function normalizeNativeError(error) {
  if (error instanceof NfcReaderError) return error;
  const code = error?.code || 'NFC_NATIVE_ERROR';
  const message = error?.message || 'Lettura NFC non riuscita.';
  return new NfcReaderError(code, message, error);
}

function availabilityValue(value) {
  if (typeof value === 'boolean') return value;
  if (!value || typeof value !== 'object') return false;
  return value.available === true && value.enabled !== false;
}

function normalizeScanResult(result) {
  if (!result || typeof result !== 'object') {
    throw new NfcReaderError(
      NFC_READER_ERRORS.INVALID_RESULT,
      'Il reader NFC non ha restituito un risultato valido.',
    );
  }

  const keys = Object.keys(result);
  if (keys.some((key) => key !== 'uri' && key !== 'sunUrl') || !result.uri && !result.sunUrl) {
    throw new NfcReaderError(
      NFC_READER_ERRORS.INVALID_RESULT,
      'Il bridge NFC ha restituito dati non previsti.',
    );
  }

  return validateSunNfcUri(result.sunUrl || result.uri);
}

const PREFLIGHT_RESULT_KEYS = [
  'readOnly',
  'writeApdusEnabled',
  'writesAttempted',
  'version',
  'fileSettings',
  'ndef',
  'tagTamperStatus',
  'profileMatches',
];

function hasExactKeys(value, keys) {
  return value
    && typeof value === 'object'
    && !Array.isArray(value)
    && Object.keys(value).length === keys.length
    && keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}

export function validateNfcPreflightResult(result) {
  if (
    !hasExactKeys(result, PREFLIGHT_RESULT_KEYS)
    || result.readOnly !== true
    || result.writeApdusEnabled !== false
    || result.writesAttempted !== false
    || typeof result.profileMatches !== 'boolean'
    || result.tagTamperStatus !== 'NOT_READ_AUTH_REQUIRED'
    || !hasExactKeys(result.version, ['complete', 'ntag424Family'])
    || result.version.complete !== true
    || result.version.ntag424Family !== true
    || !hasExactKeys(result.fileSettings, ['readable', 'matchesProfile', 'ndefFileSize', 'sdmEnabled'])
    || result.fileSettings.readable !== true
    || typeof result.fileSettings.matchesProfile !== 'boolean'
    || !Number.isInteger(result.fileSettings.ndefFileSize)
    || result.fileSettings.ndefFileSize <= 0
    || typeof result.fileSettings.sdmEnabled !== 'boolean'
    || !hasExactKeys(result.ndef, ['readable', 'recordCount', 'legacyUriRecordCount', 'capacityBytes', 'restaurantSunUriPresent'])
    || result.ndef.readable !== true
    || !Number.isInteger(result.ndef.recordCount)
    || result.ndef.recordCount < 0
    || !Number.isInteger(result.ndef.capacityBytes)
    || result.ndef.capacityBytes <= 0
    || !Number.isInteger(result.ndef.legacyUriRecordCount)
    || result.ndef.legacyUriRecordCount < 0
    || (
      result.ndef.recordCount !== null
      && result.ndef.legacyUriRecordCount > result.ndef.recordCount
    )
    || typeof result.ndef.restaurantSunUriPresent !== 'boolean'
  ) {
    throw new NfcReaderError(
      NFC_READER_ERRORS.PREFLIGHT_INVALID_RESULT,
      'Il preflight NFC ha restituito un risultato non previsto.',
    );
  }

  return result;
}

export async function runNfcReadOnlyPreflight(pluginOverride) {
  const plugin = pluginOverride || getNativeNfcPlugin();
  if (!plugin || typeof plugin.runReadOnlyPreflight !== 'function') {
    throw new NfcReaderError(
      NFC_READER_ERRORS.PREFLIGHT_UNAVAILABLE,
      'La verifica NFC in sola lettura è disponibile solo nell’app iOS.',
    );
  }

  const safeFailureStages = new Map([
    ['GetVersion non valido o tag non compatibile', 'Il tag non ha superato il controllo del modello NTAG424 DNA.'],
    ['Selezione dell’applicazione NDEF non valida', 'iOS non è riuscito a selezionare l’applicazione NDEF del tag.'],
    ['GetFileSettings non valido o non accessibile senza autenticazione', 'Le impostazioni del file NDEF non sono leggibili senza autenticazione.'],
    ['Stato NDEF non valido o non leggibile', 'iOS non ha rilevato uno stato NDEF leggibile sul tag.'],
    ['Lettura NDEF non riuscita', 'iOS ha rilevato il tag, ma non è riuscito a leggere il messaggio NDEF.'],
  ]);

  try {
    return validateNfcPreflightResult(await plugin.runReadOnlyPreflight());
  } catch (error) {
    if (error instanceof NfcReaderError) throw error;
    const safeCodes = [
      NFC_READER_ERRORS.NOT_AVAILABLE,
      NFC_READER_ERRORS.CANCELLED,
      'NFC_SCAN_IN_PROGRESS',
    ];
    const safeStage = error?.code === 'NFC_PREFLIGHT_ERROR'
      ? safeFailureStages.get(error?.message)
      : null;
    throw new NfcReaderError(
      safeCodes.includes(error?.code) ? error.code : 'NFC_PREFLIGHT_ERROR',
      `${safeStage || 'Verifica NFC non riuscita.'} Nessuna scrittura è stata eseguita.`,
    );
  }
}

export class NfcReader {
  constructor({ plugin, timeoutMs = 15000 } = {}) {
    this.plugin = plugin;
    this.timeoutMs = timeoutMs;
    this.state = NFC_READER_STATES.IDLE;
    this.currentScan = null;
  }

  getPlugin() {
    return this.plugin || getNativeNfcPlugin();
  }

  async isAvailable() {
    const plugin = this.getPlugin();
    if (!plugin || typeof plugin.isAvailable !== 'function') return false;

    try {
      return availabilityValue(await plugin.isAvailable());
    } catch (error) {
      return false;
    }
  }

  async startScan() {
    if (this.currentScan) {
      throw new NfcReaderError(
        NFC_READER_ERRORS.SCAN_IN_PROGRESS,
        'Una scansione NFC è già in corso.',
      );
    }

    const plugin = this.getPlugin();
    if (!(await this.isAvailable())) {
      this.state = NFC_READER_STATES.UNAVAILABLE;
      throw new NfcReaderError(
        NFC_READER_ERRORS.NOT_AVAILABLE,
        'NFC non disponibile su questo dispositivo.',
      );
    }

    if (!plugin || typeof plugin.startScan !== 'function') {
      this.state = NFC_READER_STATES.UNAVAILABLE;
      throw new NfcReaderError(
        NFC_READER_ERRORS.NOT_AVAILABLE,
        'Il bridge NFC nativo non è configurato.',
      );
    }

    this.state = NFC_READER_STATES.SCANNING;
    let timeoutId;
    const nativePromise = Promise.resolve()
      .then(() => plugin.startScan())
      .catch((error) => {
        throw normalizeNativeError(error);
      });
    nativePromise.catch(() => {});

    const scanPromise = new Promise((resolve, reject) => {
      timeoutId = setTimeout(() => {
        Promise.resolve(plugin.stopScan?.()).catch(() => {});
        reject(new NfcReaderError(
          NFC_READER_ERRORS.TIMEOUT,
          'La lettura NFC ha superato il tempo massimo.',
        ));
      }, this.timeoutMs);

      this.currentScan = { reject };
      nativePromise.then(resolve, reject);
    });

    try {
      const result = normalizeScanResult(await scanPromise);
      this.state = NFC_READER_STATES.URI_READ;
      return result;
    } catch (error) {
      const normalized = normalizeNativeError(error);
      this.state = normalized.code === NFC_READER_ERRORS.CANCELLED
        ? NFC_READER_STATES.CANCELLED
        : normalized.code === NFC_READER_ERRORS.NOT_AVAILABLE
          ? NFC_READER_STATES.UNAVAILABLE
          : NFC_READER_STATES.ERROR;
      throw normalized;
    } finally {
      clearTimeout(timeoutId);
      this.currentScan = null;
    }
  }

  async stopScan() {
    const activeScan = this.currentScan;
    const plugin = this.getPlugin();
    try {
      await plugin?.stopScan?.();
    } catch (error) {
      // The local promise still receives the deterministic cancellation state.
    }
    if (activeScan) {
      activeScan.reject(new NfcReaderError(
        NFC_READER_ERRORS.CANCELLED,
        'La scansione NFC è stata annullata.',
      ));
    } else {
      this.state = NFC_READER_STATES.IDLE;
    }
  }
}

export function createNfcReader(options) {
  return new NfcReader(options);
}

export const nfcReader = createNfcReader();